import { BaseLocationType } from '../models/BaseLocation';
import { MediaTypeEnum } from '../gql/schema';
import {
  ContactType,
  type BaseInstanceConfig,
} from '../models/BaseInstanceConfig';
import { BaseTourDifficulty } from '../models/BaseTourDifficulty';
import { WhlConsentManagerType } from '../models/WhlConsentManagerType';
import { ConsentVendor } from '@utils/consentManager/consent-manager';
import { Modules } from '@models/Modules';

enum NrwtMediaTypes {
  STANDARD = 1,
  VORSCHAUBILD = 2,
  HERO_BILD = 3,
  VIDEO_VORSCHAU = 4,
  IMPORTBILD = 5,
  DEFAULT_BILD_DATAHUB = 6,
  GALLERY_BILD_DATAHUB = 7,
}

enum NrwtTourCategories {
  AKTIV_UND_OUTDOOR = 14,
  RADFAHREN = 2,
  WANDERN = 1,

  // Aktiv & Outdoor
  BARRIEREFREI = 15,
  BOOTS_UND_KANUTOUR = 16,
  INLINERTOUR = 17,
  JOGGING = 18,
  MOTORISIERTE_TOUR = 19,
  PFERDETOUR = 20,
  THEMENSTRAßE = 21,
  WINTERTOUR = 22,

  // Radfahren
  FERNRADWEG = 11,
  FLUSSRADWEG = 12,
  GRAVELBIKE = 4,
  MOUNTAINBIKE = 5,
  REGIONALE_RADTOUR = 13,
  RENNRAD = 3,

  // Wandern
  FERNWANDERWEG = 6,
  KLETTERSTEIG = 7,
  REGIONALER_WANDERWEG = 8,
  STADTRUNDGANG = 9,
  THEMENWEG = 10,
}

export const NrwtProductlines = {
  REISEN_FUER_ALLE: 100016000,
} as const;
export type NrwtProductline =
  (typeof NrwtProductlines)[keyof typeof NrwtProductlines];

export enum ReisenFuerAlleCriteria {
  GEHBEHINDERUNG = 1,
  GEHBEHINDERUNG_TEILWEISE = 2,
  HOERBEHINDERUNG = 3,
  HOERBEHINDERUNG_TEILWEISE = 4,
  SEHBEHINDERUNG = 5,
  SEHBEHINDERUNG_TEILWEISE = 6,
  ROLLSTUHLFAHRER = 7,
  ROLLSTUHLFAHRER_TEILWEISE = 8,
  BLIND = 9,
  BLIND_TEILWEISE = 10,
  KOGNITIVEN_BEEINTRAECHTIGUNGEN = 11,
  GEHOERLOS = 14,
  GEHOERLOS_TEILWEISE = 15,
}

export const CONFIG: Omit<BaseInstanceConfig, 'instanceId'> = {
  activatedModules: [
    { module: Modules.RFA, plId: NrwtProductlines.REISEN_FUER_ALLE },
  ],
  aiTextPl: 201,
  bookmarks: {
    hasItinerary: true,
    itinerary: {
      boundingBox: {
        northeast: {
          latitude: 52.531,
          longitude: 9.462,
        },
        southwest: {
          latitude: 50.323,
          longitude: 5.866,
        },
      },
    },
  },
  defaultWidgetId: 1,
  defaults: {
    hero: {
      filter: {
        mediaType: [MediaTypeEnum.Image],
        imageType: { eq: NrwtMediaTypes.DEFAULT_BILD_DATAHUB },
      },
    },
    gallery: {
      filter: {
        or: [
          {
            mediaType: [MediaTypeEnum.Image],
            imageType: {
              oneOf: [NrwtMediaTypes.GALLERY_BILD_DATAHUB],
            },
          },
          { mediaType: [MediaTypeEnum.Video] },
        ],
      },
    },
    list: {
      filter: {
        mediaType: [MediaTypeEnum.Image],
        imageType: { oneOf: [NrwtMediaTypes.DEFAULT_BILD_DATAHUB] },
      },
    },
    article: {
      contact: {
        type: ContactType.CONTACT,
      },
    },
  },
  event: {
    list: {
      list: {
        hasItinerary: true,
      },
    },
  },
  footerAdditionalLogos: [
    '/assets/images/instances/nrwt/Kofinanziert von der EU_RGB_POSITIV.png',
    '/assets/images/instances/nrwt/AK_Wirtschaft, Industrie, Klimaschutz und Energie_Farbig_RGB.png',
  ],
  footerContact: {
    name: 'Tourismus NRW e.V.',
    street: 'Völklinker Straße',
    streetNo: '4',
    zipcode: '40219',
    city: 'Düsseldorf',
    email: 'info@nrw-tourismus.de',
  },
  footerLogo: '/assets/images/instances/nrwt/logo.white.svg',
  graphqlBearerToken:
    'eyJraWQiOiJpbXgtY2RhIiwidHlwIjoiSldUIiwiYWxnIjoiUlMyNTYifQ.eyJpc3MiOiJodHRwczpcL1wvbnJ3LmlteHBsYXRmb3JtLmRlXC9vYXV0aCIsInN1YiI6IjMzIiwibG5hbWUiOiJ3cy53aGl0ZWxhYmVsLXdpZGdldHMifQ.rXpW8NHiI7OnU-FnwP2xhjMnMqfFqmFo3JBP6VpJhJ9YdvlLUcQkWd2gn8bwnP_CAP4sHtI2fEnElmlelRHipFKoTPoRycRzznhcttvbLzaDme1LBqygPkU0sTmrwcWv9Sa4-1C6mwtJ3GEBtORnN_O5rUPHgmh-AIg25g0hZZmJ9F4clnVA_vukTZkI_vzZR-zXrsW3bDLzXKGNmjj7ZMOUcEb_4x7_L3C15rrnCdqDcVbqX_xKOS4iHYPwunG4AWHWij5mEmdoP-zW7gHuSSJjH4YD3H45IUogqNGQP934Rn8P0tLKSkJE-SbU_SIWZe_v1e-hQMi-UnrL-ix6ig',
  graphqlEndpoint: `https://content-delivery.imxplatform.de/nrwt/imxplatform`,
  headerLogo: '/assets/images/instances/nrwt/logo.colored.svg',
  itinerary: {
    itinerary: {
      poi: {
        productlinesRootId: 25,
        categoryBlacklist: [50],
      },
    },
  },
  map: {
    defaultCoords: {
      // nrw
      latitude: 51.493155254942224,
      longitude: 7.555403951753447,
    },
    maptoolkitApiKey: 'nrw',
  },
  pageheader: {
    search: {
      locationSearchWhitelist: [
        BaseLocationType.ADDRESS_POI_GROUP,
        BaseLocationType.LOCATION,
        BaseLocationType.REGION,
      ],
    },
  },
  poi: {
    detail: {
      hero: {
        certificates: {
          mainImage: '/assets/images/instances/nrwt/accessibility/logo.png',
          certificatesPictoMapping: {
            [ReisenFuerAlleCriteria.GEHBEHINDERUNG]: `/assets/images/instances/nrwt/accessibility/${ReisenFuerAlleCriteria.GEHBEHINDERUNG}.png`,
            [ReisenFuerAlleCriteria.GEHBEHINDERUNG_TEILWEISE]: `/assets/images/instances/nrwt/accessibility/${ReisenFuerAlleCriteria.GEHBEHINDERUNG}.png`,
            [ReisenFuerAlleCriteria.HOERBEHINDERUNG]: `/assets/images/instances/nrwt/accessibility/${ReisenFuerAlleCriteria.HOERBEHINDERUNG}.png`,
            [ReisenFuerAlleCriteria.HOERBEHINDERUNG_TEILWEISE]: `/assets/images/instances/nrwt/accessibility/${ReisenFuerAlleCriteria.HOERBEHINDERUNG}.png`,
            [ReisenFuerAlleCriteria.SEHBEHINDERUNG]: `/assets/images/instances/nrwt/accessibility/${ReisenFuerAlleCriteria.SEHBEHINDERUNG}.png`,
            [ReisenFuerAlleCriteria.SEHBEHINDERUNG_TEILWEISE]: `/assets/images/instances/nrwt/accessibility/${ReisenFuerAlleCriteria.SEHBEHINDERUNG}.png`,
            [ReisenFuerAlleCriteria.ROLLSTUHLFAHRER]: `/assets/images/instances/nrwt/accessibility/${ReisenFuerAlleCriteria.ROLLSTUHLFAHRER}.png`,
            [ReisenFuerAlleCriteria.ROLLSTUHLFAHRER_TEILWEISE]: `/assets/images/instances/nrwt/accessibility/${ReisenFuerAlleCriteria.ROLLSTUHLFAHRER}.png`,
            [ReisenFuerAlleCriteria.BLIND]: `/assets/images/instances/nrwt/accessibility/${ReisenFuerAlleCriteria.BLIND}.png`,
            [ReisenFuerAlleCriteria.BLIND_TEILWEISE]: `/assets/images/instances/nrwt/accessibility/${ReisenFuerAlleCriteria.BLIND}.png`,
            [ReisenFuerAlleCriteria.KOGNITIVEN_BEEINTRAECHTIGUNGEN]: `/assets/images/instances/nrwt/accessibility/${ReisenFuerAlleCriteria.KOGNITIVEN_BEEINTRAECHTIGUNGEN}.png`,
            [ReisenFuerAlleCriteria.GEHOERLOS]: `/assets/images/instances/nrwt/accessibility/${ReisenFuerAlleCriteria.GEHOERLOS}.png`,
            [ReisenFuerAlleCriteria.GEHOERLOS_TEILWEISE]: `/assets/images/instances/nrwt/accessibility/${ReisenFuerAlleCriteria.GEHOERLOS}.png`,
          },
        },
      },
    },
    list: {
      list: {
        hasItinerary: true,
        map: { switch: { hasItinerary: true } },
      },
    },
    openingHours: {
      hideDateRangeInfo: true,
    },
    processing: { applyProductlineContext: [201] /* KI-Texte */ },
  },
  // TODO: What are the correct gtagVendorCodes?
  portalConsentManager: {
    type: WhlConsentManagerType.CONSENT_MANAGER,
    config: {
      consentManagerId: 'a7234e5ba1e7f',
      vendorIds: {
        [ConsentVendor.MAPTOOLKIT]: 'c54313',
        [ConsentVendor.YOUTUBE]: 's30',
        [ConsentVendor.GTAG]: 's26',
      },
    } satisfies ConsentManagerConfig,
  },
  scripts: [
    {
      src: 'https://www.googletagmanager.com/gtag/js?id=G-HTTVQ75FBH',
      vendorInfo: 's26',
      async: true,
      class: 'cmplazyload',
    },
    {
      innerHTML: `
              window.dataLayer = window.dataLayer || [];  function gtag(){dataLayer.push(arguments);}  gtag('js', new Date());  gtag('config', 'G-HTTVQ75FBH');
      `,
      vendorInfo: 's26',
      class: 'cmplazyload',
    },
  ],
  tour: {
    list: {
      list: {
        hasItinerary: true,
        map: {
          switch: {
            hasItinerary: true,
          },
        },
      },
    },
    categoryIcons: {
      [NrwtTourCategories.AKTIV_UND_OUTDOOR]: 'ion:active',
      [NrwtTourCategories.MOTORISIERTE_TOUR]: 'ph:motorcycle-fill',
      [NrwtTourCategories.WINTERTOUR]: 'lets-icons:winter',
      [NrwtTourCategories.RADFAHREN]: 'ion:bicycle',
      [NrwtTourCategories.STADTRUNDGANG]: 'icon-park-solid:turn-around',
      [NrwtTourCategories.WANDERN]: 'material-symbols:hiking',
      [NrwtTourCategories.BOOTS_UND_KANUTOUR]: 'ion:boat-outline',
    },
    difficultyMapping: {
      1: BaseTourDifficulty.EASY,
      2: BaseTourDifficulty.EASY,
      3: BaseTourDifficulty.MEDIUM,
      4: BaseTourDifficulty.HARD,
      5: BaseTourDifficulty.HARD,
    },
    processing: { applyProductlineContext: [201] /* KI-Texte */ },
  },
};
